'use client'

import { useEffect, useRef } from 'react'
import { notifications } from '@mantine/notifications'

/**
 * Shared notification id for "your session has expired". Anything else that detects an expired
 * session (e.g. a 401 from the /api/graphql proxy) should reuse this id so the user sees a single
 * message rather than several stacked copies of the same news.
 */
export const SESSION_EXPIRED_NOTIFICATION_ID = 'session-expired'

type SessionExpiryWatcherProps = {
  /** The current user, or null once the session is gone. From Auth0's `useUser()`. */
  user: unknown
  isLoading: boolean
  error?: unknown
}

/**
 * Warns the user once their session lapses.
 *
 * Sessions use a rolling 30 minute inactivity window, so a user who leaves a tab open while doing
 * something else (photographing a property, reading a document) can come back to a dead session
 * with no indication anything changed. Auth0's `useUser` revalidates on window focus, so this
 * typically fires the moment they return to the tab -- before they attempt something that fails.
 *
 * Deliberately does not redirect: the user may have unsaved client-side state (staged uploads,
 * partially filled forms), and navigating them away would discard it. Reloading is genuinely cheap
 * recovery -- the Auth0 tenant session outlives the app session, so a reload usually
 * re-authenticates silently with no login prompt.
 *
 * Takes session state as props rather than calling `useUser()` itself so this package doesn't need
 * to depend on `@auth0/nextjs-auth0` -- same approach as `AppErrorBoundary` taking `trackError`.
 * Each app mounts a thin wrapper that supplies the hook result.
 */
export default function SessionExpiryWatcher({ user, isLoading, error }: SessionExpiryWatcherProps) {
  const wasAuthenticatedRef = useRef(false)

  useEffect(() => {
    if (user && !isLoading) {
      wasAuthenticatedRef.current = true
    }

    // Only show when user becomes null without an error — this is the 204 response
    // from /auth/profile when the session cookie is gone (true session expiry).
    // When there IS an error (network failure, server error), skip the notification
    // since SWR will retry and it may resolve on its own.
    if (wasAuthenticatedRef.current && !user && !isLoading && !error) {
      // Plain Mantine notification rather than notifyWarn: the white card with a yellow accent bar
      // is quieter than notifyWarn's filled tan style, and matches what backyard2 showed before
      // this component was shared.
      notifications.show({
        id: SESSION_EXPIRED_NOTIFICATION_ID,
        title: 'Session expired',
        message: 'Your session has expired. Please reload the page.',
        color: 'yellow',
        autoClose: false,
        withCloseButton: true,
      })
      wasAuthenticatedRef.current = false
    }
  }, [user, isLoading, error])

  return null
}
